import type { HttpHandler } from 'msw';

import { createOutstandingBalanceOverrides } from './outstanding-balance';
import { createConnectingFlightOverrides } from './connecting-flight';
import { createAddAddonsOverrides } from './add-addons';
import { createAvailabilityAncillariesOverrides } from './availability-ancillaries';
import { createBookingFeaturesOverrides } from './booking-features';
import { createBookingAncillariesOverrides } from './booking-ancillaries';
import { createQuotationAncillariesOverrides } from './quotation-ancillaries';
import { createQuotationQuotaOverrides } from './quotation-quota';
import { createBookingConfirmQuotaOverrides } from './booking-confirm-quota';
import { createBookingConfirmFailOnceOverrides } from './booking-confirm-fail-once';
import { createBookingPayment3dsChallengeOverrides } from './booking-payment-3ds-challenge';
import { createBookingPayment3dsRefusedOverrides } from './booking-payment-3ds-refused';
import { createBookingPayment3dsTimeoutOverrides } from './booking-payment-3ds-timeout';
import { createBookingDetailConfirmableOverrides } from './booking-detail-confirmable';
import { createBookingDetailQuotaOverrides } from './booking-detail-quota';
import { createAncillaryInjectionFailedOverrides } from './ancillary-injection-failed';
import { createIncludedAncillaryNotAvailableOverrides } from './included-ancillary-not-available';
import { createListBookingsNoBreakdownOverrides } from './list-bookings-no-breakdown';
import { createListBookingsQuotaBeforeBookingOverrides } from './list-bookings-quota-before-booking';
import { createInsufficientQuotaOverrides } from './insufficient-quota';
import { createInsufficientQuotaReturnOverrides } from './insufficient-quota-return';
import { createBillingReceiptsOverrides } from './billing-receipts';
import { createBillingReceiptDiscountOverrides } from './billing-receipt-discount';
import { createBillingReceiptManyItemsOverrides } from './billing-receipt-many-items';
import { createAvailabilityNotFoundOverrides } from './availability-not-found';
import { createPlansCampaignDiscountsOverrides } from './plans-campaign-discounts';
import { createAvailabilityBrowseOverrides } from './availability-browse';
import { createBookingEngineErrorOverrides } from './booking-engine-error';
import { createMultipleSubscriptionsOverrides } from './multiple-subscriptions';

export interface MSWOverridesConfig {
  OUTSTANDING_BALANCE: boolean;
  CONNECTING_FLIGHT: boolean;
  ADD_ADDONS: boolean;
  AVAILABILITY_ANCILLARIES: boolean;
  BOOKING_FEATURES: boolean;
  BOOKING_ANCILLARIES: boolean;
  QUOTATION_ANCILLARIES: boolean;
  QUOTATION_QUOTA: boolean;
  BOOKING_CONFIRM_QUOTA: boolean;
  BOOKING_CONFIRM_FAIL_ONCE: boolean;
  BOOKING_PAYMENT_3DS_CHALLENGE: boolean;
  BOOKING_PAYMENT_3DS_REFUSED: boolean;
  BOOKING_PAYMENT_3DS_TIMEOUT: boolean;
  BOOKING_DETAIL_CONFIRMABLE: boolean;
  BOOKING_DETAIL_QUOTA: boolean;
  ANCILLARY_INJECTION_FAILED: boolean;
  INCLUDED_ANCILLARY_NOT_AVAILABLE: boolean;
  LIST_BOOKINGS_NO_BREAKDOWN: boolean;
  LIST_BOOKINGS_QUOTA_BEFORE_BOOKING: boolean;
  INSUFFICIENT_QUOTA: boolean;
  INSUFFICIENT_QUOTA_RETURN: boolean;
  BILLING_RECEIPTS: boolean;
  BILLING_RECEIPT_DISCOUNT: boolean;
  BILLING_RECEIPT_MANY_ITEMS: boolean;
  AVAILABILITY_NOT_FOUND: boolean;
  PLANS_CAMPAIGN_DISCOUNTS: boolean;
  AVAILABILITY_BROWSE: boolean;
  BOOKING_ENGINE_ERROR: boolean;
  MULTIPLE_SUBSCRIPTIONS: boolean;
}

/**
 * Returns only the MSW override handlers that are enabled in the config.
 *
 * To add a new override, create a factory module, add its key to
 * MSWOverridesConfig, and register it in the map below.
 * Enable it at runtime via NUXT_PUBLIC_MSW_OVERRIDES_<KEY>=true.
 */
type OverrideFactory = (baseUrl: string) => HttpHandler[];

export function createSelectedOverrides(baseUrl: string, overrides: MSWOverridesConfig): HttpHandler[] {
  const registry: Array<[boolean, OverrideFactory]> = [
    [overrides.OUTSTANDING_BALANCE, createOutstandingBalanceOverrides],
    [overrides.CONNECTING_FLIGHT, createConnectingFlightOverrides],
    [overrides.ADD_ADDONS, createAddAddonsOverrides],
    [overrides.AVAILABILITY_ANCILLARIES, createAvailabilityAncillariesOverrides],
    [overrides.BOOKING_FEATURES, createBookingFeaturesOverrides],
    [overrides.BOOKING_ANCILLARIES, createBookingAncillariesOverrides],
    [overrides.QUOTATION_ANCILLARIES, createQuotationAncillariesOverrides],
    [overrides.QUOTATION_QUOTA, createQuotationQuotaOverrides],
    [overrides.BOOKING_CONFIRM_QUOTA, createBookingConfirmQuotaOverrides],
    [overrides.BOOKING_CONFIRM_FAIL_ONCE, createBookingConfirmFailOnceOverrides],
    [overrides.BOOKING_PAYMENT_3DS_CHALLENGE, createBookingPayment3dsChallengeOverrides],
    [overrides.BOOKING_PAYMENT_3DS_REFUSED, createBookingPayment3dsRefusedOverrides],
    [overrides.BOOKING_PAYMENT_3DS_TIMEOUT, createBookingPayment3dsTimeoutOverrides],
    [overrides.BOOKING_DETAIL_CONFIRMABLE, createBookingDetailConfirmableOverrides],
    [overrides.BOOKING_DETAIL_QUOTA, createBookingDetailQuotaOverrides],
    [overrides.ANCILLARY_INJECTION_FAILED, createAncillaryInjectionFailedOverrides],
    [overrides.INCLUDED_ANCILLARY_NOT_AVAILABLE, createIncludedAncillaryNotAvailableOverrides],
    [overrides.LIST_BOOKINGS_NO_BREAKDOWN, createListBookingsNoBreakdownOverrides],
    [overrides.LIST_BOOKINGS_QUOTA_BEFORE_BOOKING, createListBookingsQuotaBeforeBookingOverrides],
    [overrides.INSUFFICIENT_QUOTA, createInsufficientQuotaOverrides],
    [overrides.INSUFFICIENT_QUOTA_RETURN, createInsufficientQuotaReturnOverrides],
    [overrides.BILLING_RECEIPTS, createBillingReceiptsOverrides],
    [overrides.BILLING_RECEIPT_DISCOUNT, createBillingReceiptDiscountOverrides],
    [overrides.BILLING_RECEIPT_MANY_ITEMS, createBillingReceiptManyItemsOverrides],
    [overrides.AVAILABILITY_NOT_FOUND, createAvailabilityNotFoundOverrides],
    [overrides.PLANS_CAMPAIGN_DISCOUNTS, createPlansCampaignDiscountsOverrides],
    [overrides.AVAILABILITY_BROWSE, createAvailabilityBrowseOverrides],
    [overrides.BOOKING_ENGINE_ERROR, createBookingEngineErrorOverrides],
    [overrides.MULTIPLE_SUBSCRIPTIONS, createMultipleSubscriptionsOverrides],
  ];

  return registry.flatMap(([enabled, factory]) => (enabled ? factory(baseUrl) : []));
}
